import { useState } from "react";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { AutoResizeTextarea } from "@/components/ui/auto-resize-textarea";
import { MessageAvatar } from "./MessageAvatar";
import { Trash2, ArrowLeftFromLine, ArrowRightFromLine } from "lucide-react";

interface MessageBubbleInputProps {
  isUser: boolean;
  isEditing?: boolean;
  userName: string;
  targetName: string;
  onClose: () => void;
  onSave: (message: string) => void;
  initialMessage?: string;
  onDelete?: () => void;
  onMove?: () => void;
}

export const MessageBubbleInput = ({
  isUser,
  isEditing = false,
  userName,
  targetName,
  onClose,
  onSave,
  initialMessage,
  onDelete,
  onMove,
}: MessageBubbleInputProps) => {
  const [newMessageInput, setNewMessageInput] = useState(initialMessage || "");

  return (
    <div
      className={`flex w-full gap-2 ${
        isUser ? "flex-row-reverse" : "flex-row"
      }`}
    >
      <MessageAvatar
        isUser={isUser}
        userName={userName}
        targetName={targetName}
      />
      <div
        className={cn(
          "mb-4 rounded-3xl gap-3 items-start w-full sm:w-3/4 shadow",
          !isUser
            ? "bg-gradient-to-b from-gray-200  from-50%  to-gray-300 text-gray-900 "
            : "bg-gradient-to-b from-gray-700  from-50%  to-gray-900 "
        )}
      >
        <div className="flex-1 px-2 py-1">
          <AutoResizeTextarea
            value={newMessageInput}
            onChange={(e) => setNewMessageInput(e.target.value)}
            placeholder={`Type message from ${isUser ? "you" : targetName}...`}
            rows={1}
            autoFocus
            className={cn(
              "text-base resize-none shadow-none max-h-48 border-none focus-visible:ring-0 rounded-3xl min-h-20 sm:min-h-12",
              !isUser ? "" : " text-white placeholder:text-gray-400"
            )}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSave(newMessageInput);
              } else if (e.key === "Escape") {
                onClose();
              }
            }}
          />
          <div className="flex justify-between items-center p-2 pt-0">
            {isEditing && (
              <div className="flex gap-2">
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={onDelete}
                  className="h-8 w-8 focus:ring-0 focus:ring-offset-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="sr-only">Delete message</span>
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={onMove}
                  className={`h-8 w-8 focus:ring-0 focus:ring-offset-0 ${
                    isUser
                      ? "text-gray-100 hover:text-gray-50 hover:bg-gray-600"
                      : "text-gray-900 hover:text-gray-950 hover:bg-gray-100"
                  } `}
                >
                  {isUser ? (
                    <ArrowLeftFromLine className="h-4 w-4" />
                  ) : (
                    <ArrowRightFromLine className="h-4 w-4" />
                  )}
                  <span className="sr-only">Move message</span>
                </Button>
              </div>
            )}
            <div className="flex gap-2 ml-auto">
              <Button
                size="sm"
                variant={isUser ? undefined : "outline"}
                className={`rounded-full border ${
                  isUser ? "border-gray-600" : "border-gray-300"
                }`}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                variant={isUser ? "outline" : undefined}
                size="sm"
                className="rounded-full"
                onClick={() => onSave(newMessageInput)}
              >
                {isEditing ? "Save Message" : "Add Message"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
